import { useEffect, useState, useRef, useCallback } from 'react';

interface HeroAnimationProps {
  onComplete: () => void;
  onSkip: () => void;
}

// Timings (ms) for how long each frame is fully visible before cross-fading to next.
// The CSS transition in index.css handles the 0.9s fade — no JS needed for that.
const FRAME_DURATIONS = [2800, 3000, 3000, 2500, 3200, 99999];

export default function HeroAnimation({ onComplete, onSkip }: HeroAnimationProps) {
  const [frame, setFrame]           = useState(1);
  const [words5a, setWords5a]       = useState<boolean[]>([false, false, false]); // "Set it once."
  const [words5b, setWords5b]       = useState<boolean[]>([false, false, false, false]); // "Never miss a moment."
  const [showSkip, setShowSkip]     = useState(false);
  const timerRef                    = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read once on mount — stable for the lifetime of this component.
  // Guarded for server-side pre-rendering (vite-react-ssg) where `window`
  // is undefined; defaults to "motion allowed" until the client hydrates.
  const prefersReduced              = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  ).current;

  const goToFrame = useCallback((n: number) => {
    setFrame(n);
    if (n === 6) onComplete();
  }, [onComplete]);

  const skipToEnd = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    goToFrame(6);
    onSkip();
  }, [goToFrame, onSkip]);

  // If reduced motion — skip straight to frame 6
  useEffect(() => {
    if (prefersReduced) { goToFrame(6); return; }

    // Show skip button immediately
    const skipTimer = setTimeout(() => setShowSkip(true), 0);
    return () => clearTimeout(skipTimer);
  }, [prefersReduced, goToFrame]);

  // Advance through frames 1 → 6
  useEffect(() => {
    if (prefersReduced || frame >= 6) return;

    timerRef.current = setTimeout(() => {
      goToFrame(frame + 1);
    }, FRAME_DURATIONS[frame - 1]);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [frame, prefersReduced, goToFrame]);

  // Frame 5: animate words in two lines
  useEffect(() => {
    if (frame !== 5) return;
    const line1Words = [0, 1, 2];
    const line2Words = [0, 1, 2, 3];

    // Line 1 ("Set it once.") — staggered 200ms apart
    line1Words.forEach((i) => {
      setTimeout(() => setWords5a(prev => { const n = [...prev]; n[i] = true; return n; }), 400 + i * 220);
    });
    // Line 2 ("Never miss a moment.") — starts after line 1
    line2Words.forEach((i) => {
      setTimeout(() => setWords5b(prev => { const n = [...prev]; n[i] = true; return n; }), 1600 + i * 220);
    });
  }, [frame]);

  const isActive = (n: number) => frame === n;

  return (
    <div className="relative w-full h-full overflow-hidden bg-cream">

      {/* ── Skip button ───────────────────────────────────────────────────── */}
      {showSkip && frame < 6 && (
        <button
          onClick={skipToEnd}
          className="absolute top-20 right-6 z-30 text-sm font-medium text-gray-500
                     hover:text-navy transition-colors duration-200 flex items-center gap-1
                     bg-white/10 px-3 py-1.5 rounded-full"
          aria-label="Skip animation"
        >
          Skip intro →
        </button>
      )}

      {/* ╔══════════════════════════════════════════════════════════╗
          ║  FRAME 1, The Problem (hook)                           ║
          ╚══════════════════════════════════════════════════════════╝ */}
      <div className={`hero-frame ${isActive(1) ? 'active' : 'inactive'}`} aria-hidden={!isActive(1)}>
        <div className="flex flex-col items-center gap-8 text-center px-6 max-w-sm">

          {/* Calendar illustration — yesterday's birthday circled, missed */}
          <div className="relative animate-fade-in">
            <svg width="148" height="140" viewBox="0 0 148 140" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Calendar body */}
              <rect x="4" y="20" width="140" height="112" rx="12" fill="white" stroke="#E8E0D5" strokeWidth="1.5"/>
              {/* Header bar */}
              <rect x="4" y="20" width="140" height="32" rx="12" fill="#1A1410"/>
              <rect x="4" y="36" width="140" height="16" fill="#1A1410"/>
              {/* Calendar rings */}
              <rect x="40" y="12" width="8" height="18" rx="4" fill="#E8C12E"/>
              <rect x="100" y="12" width="8" height="18" rx="4" fill="#E8C12E"/>
              {/* Month label */}
              <text x="74" y="41" textAnchor="middle" fontSize="11" fontWeight="600" fill="white" fontFamily="sans-serif">May</text>
              {/* Day grid */}
              {/* Row 1 */}
              <rect x="16" y="60" width="16" height="16" rx="4" fill="#F5F5F5"/>
              <text x="24" y="72" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="sans-serif">5</text>
              <rect x="38" y="60" width="16" height="16" rx="4" fill="#F5F5F5"/>
              <text x="46" y="72" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="sans-serif">6</text>
              <rect x="60" y="60" width="16" height="16" rx="4" fill="#F5F5F5"/>
              <text x="68" y="72" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="sans-serif">7</text>
              <rect x="82" y="60" width="16" height="16" rx="4" fill="#F5F5F5"/>
              <text x="90" y="72" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="sans-serif">8</text>
              {/* Yesterday — missed birthday, circled in soft red */}
              <rect x="104" y="58" width="20" height="20" rx="5" fill="#FEE2E2"/>
              <text x="114" y="72" textAnchor="middle" fontSize="9" fontWeight="700" fill="#EF4444" fontFamily="sans-serif">9</text>
              {/* Tiny cake on missed day */}
              <text x="114" y="83" textAnchor="middle" fontSize="7" fontFamily="sans-serif">🎂</text>
              {/* TODAY marker */}
              <rect x="16" y="84" width="20" height="20" rx="5" fill="#1A1410"/>
              <text x="26" y="98" textAnchor="middle" fontSize="9" fontWeight="700" fill="white" fontFamily="sans-serif">10</text>
              <rect x="42" y="86" width="16" height="16" rx="4" fill="#F5F5F5"/>
              <text x="50" y="98" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="sans-serif">11</text>
              <rect x="64" y="86" width="16" height="16" rx="4" fill="#F5F5F5"/>
              <text x="72" y="98" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="sans-serif">12</text>
              <rect x="86" y="86" width="16" height="16" rx="4" fill="#F5F5F5"/>
              <text x="94" y="98" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="sans-serif">13</text>
              <rect x="108" y="86" width="16" height="16" rx="4" fill="#F5F5F5"/>
              <text x="116" y="98" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="sans-serif">14</text>
              {/* Missed label */}
              <text x="114" y="118" textAnchor="middle" fontSize="8" fill="#EF4444" fontFamily="sans-serif" fontStyle="italic">missed</text>
            </svg>
          </div>

          {/* Hook copy */}
          <div className="space-y-3 animate-fade-in" style={{ animationDelay: '0.5s', animationFillMode: 'both', opacity: 0 }}>
            <p className="font-heading text-3xl sm:text-4xl font-bold text-navy leading-tight">
              Life gets busy.
            </p>
            <p className="font-accent italic text-lg text-gray-500 leading-snug">
              We send the card. You get the credit.
            </p>
            <p className="font-body text-sm text-gray-400 leading-snug">
              Real, printed cards, mailed automatically for every occasion.
            </p>
          </div>
        </div>
      </div>

      {/* ╔══════════════════════════════════════════════════════════╗
          ║  FRAME 2, Card slides out of envelope                  ║
          ╚══════════════════════════════════════════════════════════╝ */}
      <div className={`hero-frame ${isActive(2) ? 'active' : 'inactive'}`} aria-hidden={!isActive(2)}>
        <div className="flex flex-col items-center gap-10">
          {/* Envelope with card peeking out */}
          <div className="relative" style={{ width: 160, height: 160 }}>
            {/* Card peeking up */}
            <div className="absolute card-peek" style={{ top: 0, left: '50%', transform: 'translateX(-50%)' }}>
              <svg width="110" height="70" viewBox="0 0 110 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="110" height="70" rx="6" fill="#FFFFFF" stroke="#F0EBE3" strokeWidth="1" />
                {/* Card design placeholder — floral accent */}
                <circle cx="88" cy="14" r="18" fill="#FAEED8" opacity="0.2" />
                <circle cx="98" cy="22" r="12" fill="#B59518" opacity="0.15" />
                <text x="12" y="36" fontSize="11" fill="#1A1410" fontFamily="Georgia, serif" fontStyle="italic">Happy Birthday</text>
                <path d="M12 46 Q55 38 98 46" stroke="#FAEED8" strokeWidth="1" fill="none" opacity="0.5" />
              </svg>
            </div>
            {/* Envelope body */}
            <div className="absolute" style={{ bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
              <svg width="130" height="85" viewBox="0 0 130 85" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="130" height="85" rx="8" fill="#1A1410" />
                {/* Envelope flap (open) */}
                <path d="M0 0 L65 36 L130 0" fill="#243659" />
                <path d="M0 85 L65 50 L130 85" fill="#243659" />
                <rect x="0" y="0" width="130" height="4" fill="#E8C12E" rx="2" />
              </svg>
            </div>
          </div>

          {/* "They remembered me." */}
          <div className="text-center space-y-2 animate-fade-up" style={{ animationDelay: '0.8s', animationFillMode: 'both', opacity: 0 }}>
            <p className="font-accent italic text-2xl sm:text-3xl text-navy leading-snug">
              "They remembered me."
            </p>
            <p className="font-body text-sm text-gray-400">
              A real printed card, in their mailbox, from you.
            </p>
          </div>
        </div>
      </div>

      {/* ╔══════════════════════════════════════════════════════════╗
          ║  FRAME 3, Phone notification                           ║
          ╚══════════════════════════════════════════════════════════╝ */}
      <div className={`hero-frame ${isActive(3) ? 'active' : 'inactive'}`} aria-hidden={!isActive(3)}>
        <div className="flex flex-col items-center gap-5">

          {/* Phone — realistic ~1:2 proportions, 200px wide gives enough room */}
          <div className="relative" style={{ width: 200, height: 340 }}>

            {/* Phone shell + hardware buttons */}
            <svg width="200" height="340" viewBox="0 0 200 340" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Main shell */}
              <rect x="14" y="2" width="172" height="336" rx="22" fill="#1A1410" />
              {/* Subtle edge highlight */}
              <rect x="14" y="2" width="172" height="336" rx="22" fill="none"
                    stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              {/* Left — volume up */}
              <rect x="6" y="78" width="8" height="24" rx="4" fill="#14213A" />
              {/* Left — volume down */}
              <rect x="6" y="112" width="8" height="24" rx="4" fill="#14213A" />
              {/* Right — power/lock */}
              <rect x="186" y="96" width="8" height="32" rx="4" fill="#14213A" />
              {/* Screen glass */}
              <rect x="20" y="8" width="160" height="324" rx="17" fill="#F4F4F6" />
              {/* Dynamic island — small centered pill */}
              <rect x="82" y="15" width="36" height="13" rx="6.5" fill="#1A1410" />
              {/* Home indicator */}
              <rect x="80" y="323" width="40" height="4" rx="2" fill="#D1D5DB" />
            </svg>

            {/* ── SMS thread — native Messages app look ── */}
            <div className="absolute" style={{ top: 36, left: 20, right: 20 }}>

              {/* Messages app header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                paddingBottom: 8, marginBottom: 8,
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}>
                {/* Back chevron */}
                <svg width="7" height="11" viewBox="0 0 7 11" fill="none">
                  <path d="M5.5 1L1 5.5L5.5 10" stroke="#E8C12E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {/* Avatar */}
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #E8C12E 0%, #FAEED8 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
                }}>💌</div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#1A1410', margin: 0, lineHeight: 1 }}>MomentSent</p>
                  <p style={{ fontSize: 8, color: '#9CA3AF', margin: '2px 0 0', lineHeight: 1 }}>Text Message</p>
                </div>
                {/* Phone icon */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="#E8C12E" opacity="0.6"/>
                </svg>
              </div>

              {/* Date stamp */}
              <p style={{ fontSize: 8, color: '#9CA3AF', textAlign: 'center', marginBottom: 10, letterSpacing: '0.02em' }}>
                Today 9:41 AM
              </p>

              {/* Incoming SMS bubble */}
              <div className="animate-fade-up" style={{ animationDelay: '0.3s', animationFillMode: 'both', opacity: 0 }}>
                <div style={{
                  background: '#E8E8EA',
                  borderRadius: '14px 14px 14px 3px',
                  padding: '8px 10px',
                  maxWidth: '88%',
                  marginBottom: 5,
                }}>
                  <p style={{ fontSize: 10, color: '#1C1C1E', lineHeight: 1.45, margin: 0 }}>
                    📅 <strong>Dad's birthday is in 14 days.</strong> Time to review your card and lock in your personal message!
                  </p>
                </div>

                {/* Rich link preview — "tap to review" card */}
                <div style={{
                  maxWidth: '88%',
                  borderRadius: '10px 10px 10px 3px',
                  overflow: 'hidden',
                  border: '1px solid #E5E7EB',
                  background: '#FFFFFF',
                  marginBottom: 5,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ background: '#E8C12E', padding: '5px 9px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10 }}>💌</span>
                    <span style={{ fontSize: 9, color: 'white', fontWeight: 600, letterSpacing: '0.02em' }}>momentsent.com</span>
                  </div>
                  <div style={{ padding: '6px 9px' }}>
                    <p style={{ fontSize: 10, fontWeight: 700, color: '#1A1410', margin: 0, lineHeight: 1.3 }}>
                      Review your card →
                    </p>
                    <p style={{ fontSize: 8, color: '#9CA3AF', margin: '3px 0 0', lineHeight: 1.3 }}>
                      Pick a design · Add your message · Approve
                    </p>
                  </div>
                </div>

                {/* Delivered receipt */}
                <p style={{ fontSize: 8, color: '#9CA3AF', paddingLeft: 2 }}>Delivered</p>
              </div>
            </div>
          </div>

          {/* Caption */}
          <div className="text-center space-y-1">
            <p className="font-accent italic text-lg text-navy">
              14 days out, your cue to pick, personalize, and approve.
            </p>
            <p className="font-body text-xs text-gray-400">
              Nothing ever mails without your sign-off.
            </p>
          </div>
        </div>
      </div>

      {/* ╔══════════════════════════════════════════════════════════╗
          ║  FRAME 4, "You didn't have to think about it."         ║
          ╚══════════════════════════════════════════════════════════╝ */}
      <div className={`hero-frame ${isActive(4) ? 'active' : 'inactive'}`} aria-hidden={!isActive(4)}>
        <div className="text-center max-w-sm px-6">
          {/* Abstract desk scene */}
          <div className="mb-8 flex justify-center">
            <svg width="120" height="80" viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-60">
              {/* Desk surface */}
              <rect x="0" y="56" width="120" height="6" rx="3" fill="#E8E0D5" />
              {/* Laptop */}
              <rect x="28" y="28" width="64" height="38" rx="4" fill="#1A1410" opacity="0.7" />
              <rect x="32" y="32" width="56" height="30" rx="2" fill="#E8C12E" opacity="0.2" />
              <rect x="20" y="60" width="80" height="4" rx="2" fill="#1A1410" opacity="0.5" />
              {/* Coffee cup */}
              <rect x="92" y="38" width="20" height="22" rx="4" fill="#B59518" opacity="0.4" />
              <path d="M112 44 Q120 44 120 52 Q120 60 112 60" stroke="#B59518" strokeWidth="2" fill="none" opacity="0.4" />
              {/* Steam */}
              <path d="M97 36 Q99 30 97 24" stroke="#E8E0D5" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
              <path d="M103 36 Q105 28 103 22" stroke="#E8E0D5" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
              {/* Phone glow */}
              <rect x="2" y="38" width="22" height="14" rx="3" fill="#FAEED8" opacity="0.5" />
              <rect x="4" y="40" width="18" height="10" rx="2" fill="#F8F5EE" opacity="0.8" />
            </svg>
          </div>

          <p className="font-accent italic text-2xl sm:text-3xl text-navy leading-snug animate-fade-in">
            "You didn't have to think about it."
          </p>
          <p className="mt-4 font-body text-sm text-gray-400 animate-fade-in" style={{ animationDelay: '0.6s', animationFillMode: 'both', opacity: 0 }}>
            The card arrived. You were there, even when life was busy.
          </p>
        </div>
      </div>

      {/* ╔══════════════════════════════════════════════════════════╗
          ║  FRAME 5, Word-by-word text reveal                     ║
          ╚══════════════════════════════════════════════════════════╝ */}
      <div className={`hero-frame ${isActive(5) ? 'active' : 'inactive'}`} aria-hidden={!isActive(5)}>
        <div className="text-center max-w-lg px-6">
          {/* Line 1: "Set it once." */}
          <div className="font-heading text-4xl sm:text-5xl lg:text-6xl text-navy mb-4 leading-tight">
            {['Set', 'it', 'once.'].map((word, i) => (
              <span
                key={i}
                className="word-animate"
                style={{
                  opacity:          words5a[i] ? 1 : 0,
                  transform:        words5a[i] ? 'translateY(0)' : 'translateY(12px)',
                  transition:       'opacity 0.45s ease-out, transform 0.45s ease-out',
                  marginRight:      i < 2 ? '0.3em' : 0,
                  display:          'inline-block',
                  transitionDelay:  '0ms',
                }}
              >
                {word}
              </span>
            ))}
          </div>

          {/* Line 2: "Never miss a moment." */}
          <div className="font-heading text-4xl sm:text-5xl lg:text-6xl text-sage leading-tight">
            {['Never', 'miss', 'a', 'moment.'].map((word, i) => (
              <span
                key={i}
                className="word-animate"
                style={{
                  opacity:         words5b[i] ? 1 : 0,
                  transform:       words5b[i] ? 'translateY(0)' : 'translateY(12px)',
                  transition:      'opacity 0.45s ease-out, transform 0.45s ease-out',
                  marginRight:     i < 3 ? '0.3em' : 0,
                  display:         'inline-block',
                  transitionDelay: '0ms',
                }}
              >
                {word}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ╔══════════════════════════════════════════════════════════╗
          ║  FRAME 6, Logo + Tagline + Pulsing CTA (Final frame)   ║
          ╚══════════════════════════════════════════════════════════╝ */}
      <div className={`hero-frame ${isActive(6) ? 'active' : 'inactive'}`} aria-hidden={!isActive(6)}>
        <div className="text-center flex flex-col items-center gap-6 px-6">
          {/* Logo */}
          <div className="animate-fade-in">
            <span className="font-heading text-5xl sm:text-6xl font-bold text-navy tracking-tight">
              Moment<span className="text-sage">Sent</span>
            </span>
          </div>

          {/* Tagline */}
          <p
            className="font-accent italic text-xl sm:text-2xl text-navy opacity-80 animate-fade-in"
            style={{ animationDelay: '0.3s', animationFillMode: 'both', opacity: 0 }}
          >
            The card always arrives. You always care.
          </p>

          {/* Pulsing CTA */}
          <div
            className="animate-fade-in"
            style={{ animationDelay: '0.7s', animationFillMode: 'both', opacity: 0 }}
          >
            <a
              href="#signup"
              className="cta-pulse inline-flex items-center gap-2 bg-sage text-navy
                         font-body font-semibold text-base px-8 py-4 rounded-full
                         hover:bg-opacity-90 transition-colors duration-200"
              style={{ minHeight: 54 }}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <span>Start Free</span>
              <span className="text-white opacity-70">,</span>
              <span>No Card Needed</span>
            </a>
          </div>

          {/* Scroll hint */}
          <div
            className="animate-fade-in mt-2"
            style={{ animationDelay: '1.2s', animationFillMode: 'both', opacity: 0 }}
          >
            <button
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-navy transition-colors"
              aria-label="Scroll down"
            >
              <span className="text-xs font-body tracking-widest uppercase">Scroll</span>
              <svg className="scroll-bounce w-4 h-4" fill="none" viewBox="0 0 16 16">
                <path d="M8 3v10M4 9l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Frame progress indicator (frames 1-5 only) */}
      {frame < 6 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
          {[1,2,3,4,5].map((n) => (
            <div
              key={n}
              className="rounded-full transition-all duration-500"
              style={{
                width:           frame === n ? 20 : 6,
                height:          6,
                backgroundColor: frame === n ? '#E8C12E' : 'rgba(26,20,16,0.15)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
